import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHotkeys } from '../ui';
import { ART } from './art';
import { COPY, JEV, letterOf, MIN_OWN_WORDS, OWN_WORDS, type Answer, type OwnQuestion } from './config';
import { EASE_OUT, FOLD_MS, GROW_MS, reducedMotion, windowOnto } from './motion';
import styles from './case.module.css';

const RADIUS = 16;
const wordsIn = (text: string) => (text.trim().match(/\S+/g) ?? []).length;

export interface OwnWordsProps {
  /** Which question it answers, and that question's cards, for what it sounds like. */
  question: OwnQuestion;
  answers: readonly Answer<string>[];
  /** The card it grows out of, and folds back into. */
  from: DOMRect;
  /** Called once it has folded back. */
  onClose: () => void;
  /** The sentence, and the answer it sounds like unless the person waved that away. */
  onSubmit: (words: string, sounds: string | null) => void;
}

/** A faint ending the person can take with Tab: a phrase that continues the sentence, or the why. */
interface Tail {
  text: string;
  kind: 'next' | 'why';
}

interface JevAnswers {
  kind?: { choice: string; confidence: number };
  next?: { choice: string; confidence: number; probabilities?: Record<string, number> };
  why?: { noul: number };
}

/** Which corner of the growing window each piece holds on to. */
type Corner = readonly ['l' | 'r', 't' | 'b'];

/** Where a piece sits when its window is still the card: its corner of the card, not of the box. */
function heldAt(card: DOMRect, box: DOMRect, [h, v]: Corner, scale = 1) {
  const x = h === 'l' ? card.left - box.left : card.right - box.right;
  const y = v === 't' ? card.top - box.top : card.bottom - box.bottom;
  return `translate(${x / scale}px, ${y / scale}px)`;
}

/** How much an element is scaled on screen: 1 in the flow, less where the landing draws the flow smaller. */
const scaleOf = (el: HTMLElement) => el.getBoundingClientRect().width / el.offsetWidth || 1;

/** The part of a phrase not typed yet: "I convinced" and "convinced the team to" leave "the team to". */
function continuation(typed: string, phrase: string) {
  const said = typed.trim().toLowerCase().split(/\s+/);
  const words = phrase.split(' ');
  let k = Math.min(words.length, said.length);
  while (k > 0 && words.slice(0, k).join(' ').toLowerCase() !== said.slice(-k).join(' ')) k--;
  return words.slice(k).join(' ');
}

/** The why, shaped to fit where the sentence stops. */
function whyTail(typed: string) {
  const spaced = /\s$/.test(typed);
  if (/[.!?]\s*$/.test(typed)) return `${spaced ? '' : ' '}It mattered because`;
  if (/,\s*$/.test(typed) || spaced) return `${spaced ? '' : ' '}because`;
  return ', because';
}

/** Whether Jev is on, asked once per page. */
let jevOn: Promise<boolean> | null = null;
const askIfJevIsOn = () =>
  (jevOn ??= fetch('/cases/jev')
    .then((answer) => (answer.ok ? (answer.json() as Promise<{ on?: boolean }>) : { on: false }))
    .then((status) => Boolean(status.on))
    .catch(() => false));

/**
 * "Something else", as the intake prototype has it. The card grows over the others into one input: the
 * input is full size from the first frame and a window grows from the card's slot to show it, each piece
 * pinned to its corner of that window, so the card visibly becomes the input. The other cards step back,
 * nearest first. Typing can start while it grows; Enter or Esc lands it at once. After each pause Jev (or,
 * without it, plain keywords) offers what it sounds like and one faint ending that Tab takes.
 */
export function OwnWords({ question, answers, from, onClose, onSubmit }: OwnWordsProps) {
  const asks = OWN_WORDS[question];
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const was = useRef<HTMLDivElement>(null);
  const art = useRef<HTMLDivElement>(null);
  const shut = useRef<HTMLButtonElement>(null);
  const middle = useRef<HTMLDivElement>(null);
  const left = useRef<HTMLDivElement>(null);
  const send = useRef<HTMLButtonElement>(null);
  const card = useRef<HTMLElement | null>(null);
  const memo = useRef({ whyUsed: false, ignored: 0, asked: 0, timer: 0, jev: false });

  const [text, setText] = useState('');
  const [tail, setTail] = useState<Tail | null>(null);
  const [guess, setGuess] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [settled, setSettled] = useState(false);
  const [closing, setClosing] = useState(false);
  const enough = wordsIn(text) >= MIN_OWN_WORDS;

  const pinned = (): [HTMLElement | null, Corner][] => [
    [art.current, ['l', 't']],
    [shut.current, ['r', 't']],
    [middle.current, ['l', 't']],
    [left.current, ['l', 'b']],
    [send.current, ['r', 'b']],
    [was.current, ['l', 'b']],
  ];

  /** The other cards step back as it opens and come back as it folds, nearest first. */
  const stepBack = (out: boolean) => {
    const grid = box.current?.parentElement;
    if (!grid) return;
    const still = reducedMotion();
    grid.querySelectorAll<HTMLElement>(':scope > button').forEach((other) => {
      if (other === card.current) return;
      const r = other.getBoundingClientRect();
      // Nearest first, a few frames apart at most. Coming back, they wait until the folding box has
      // mostly left them, so they never show through it half drawn.
      const lag = Math.min(Math.hypot(r.left - from.left, r.top - from.top) * 0.03, 24);
      other.getAnimations().forEach((animation) => animation.cancel());
      const away = { opacity: 0, transform: still ? 'none' : 'scale(.97)' };
      const here = { opacity: 1, transform: 'none' };
      other.animate(out ? [here, away] : [away, here], {
        duration: out ? 120 : 160,
        delay: out ? lag : 70 + lag,
        easing: EASE_OUT,
        fill: out ? 'forwards' : 'backwards',
      });
    });
  };

  // Opening: the card hands over to the box, which grows out of the card's slot.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const grid = el.parentElement;
    card.current =
      [...(grid?.querySelectorAll<HTMLElement>(':scope > button') ?? [])].find((button) => {
        const r = button.getBoundingClientRect();
        return Math.abs(r.left - from.left) < 1 && Math.abs(r.top - from.top) < 1;
      }) ?? null;
    if (card.current) card.current.style.visibility = 'hidden';
    void askIfJevIsOn().then((on) => (memo.current.jev = on));
    field.current?.focus({ preventScroll: true }); // typing can start while it grows
    stepBack(true);

    if (reducedMotion()) {
      el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' }).onfinish = () => setSettled(true);
      return () => {
        if (card.current) card.current.style.visibility = '';
      };
    }
    const size = el.getBoundingClientRect();
    const k = scaleOf(el);
    el.animate([{ clipPath: windowOnto(from, size, RADIUS, k) }, { clipPath: `inset(0 0 0 0 round ${RADIUS}px)` }], {
      duration: GROW_MS,
      easing: EASE_OUT,
    }).onfinish = () => setSettled(true);
    pinned().forEach(([piece, corner]) =>
      piece?.animate([{ transform: heldAt(from, size, corner, k) }, { transform: 'none' }], { duration: GROW_MS, easing: EASE_OUT }),
    );
    // The card's own title hands over to the input's content.
    was.current?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, easing: 'ease-out', fill: 'forwards' });
    [shut.current, middle.current, left.current, send.current].forEach((piece, k) =>
      piece?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, delay: 50 + k * 15, easing: 'ease-out', fill: 'backwards' }),
    );
    return () => {
      if (card.current) card.current.style.visibility = '';
    };
    // It opens once, from the card it was given.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  useEffect(() => () => window.clearTimeout(memo.current.timer), []);

  /** Enter or Esc while it is still growing: land it at once, then act. */
  const settleNow = () => {
    if (settled) return;
    box.current?.getAnimations({ subtree: true }).forEach((animation) => animation.finish());
    setSettled(true);
  };

  const close = () => {
    const el = box.current;
    if (closing) return;
    settleNow();
    setClosing(true);
    setSettled(false);
    stepBack(false);
    if (!el || reducedMotion()) {
      if (!el) return onClose();
      el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, easing: 'ease-out', fill: 'forwards' }).onfinish = onClose;
      return;
    }
    // The card is back under the box at once: the box never shrinks smaller than it, so it only shows
    // when the box is gone, and there is nothing to swap in at the end.
    if (card.current) card.current.style.visibility = '';
    const at = card.current?.getBoundingClientRect() ?? from;
    const size = el.getBoundingClientRect();
    const k = scaleOf(el);
    // The way back is simpler than the way in: what was typed and offered goes where it is, at once, and
    // only the card's shape, its picture and its title travel back.
    [shut.current, middle.current, left.current, send.current].forEach((piece) =>
      piece?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 80, easing: 'ease-out', fill: 'forwards' }),
    );
    el.animate([{ clipPath: `inset(0 0 0 0 round ${RADIUS}px)` }, { clipPath: windowOnto(at, size, RADIUS, k) }], {
      duration: FOLD_MS,
      easing: EASE_OUT,
      fill: 'forwards',
    }).onfinish = onClose;
    ([[art.current, ['l', 't']], [was.current, ['l', 'b']]] as const).forEach(([piece, corner]) =>
      piece?.animate([{ transform: 'none' }, { transform: heldAt(at, size, corner, k) }], { duration: FOLD_MS, easing: EASE_OUT, fill: 'forwards' }),
    );
    was.current?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 100, delay: 60, easing: 'ease-out', fill: 'both' });
  };

  /** After a pause: what it sounds like, and at most one faint ending. */
  const think = async (typed: string) => {
    const el = field.current;
    if (!el || closing) return;
    const atEnd = el.selectionStart === typed.length;
    const spaced = /\s$/.test(typed);
    const asked = ++memo.current.asked;
    let answers: JevAnswers | null = null;
    if (memo.current.jev) {
      answers = await fetch('/cases/jev', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ typed, question }),
      })
        .then((answer) => (answer.ok ? (answer.json() as Promise<{ answers?: JevAnswers }>) : null))
        .then((data) => data?.answers ?? null)
        .catch(() => null);
      // They kept typing; a newer pause asks again.
      if (asked !== memo.current.asked || field.current?.value !== typed) return;
    }
    let ending: Tail | null = null;
    if (answers) {
      const { kind, next, why } = answers;
      if (kind && !dismissed) setGuess(kind.choice !== 'unclear' && kind.confidence >= JEV.bars.kind ? kind.choice : null);
      const rest = next && next.choice !== 'none' ? continuation(typed, next.choice) : '';
      const [first = 0, second = 0] = Object.values(next?.probabilities ?? {}).sort((a, b) => b - a);
      const sure = next && (next.confidence >= JEV.bars.next || (first >= JEV.bars.lead && first - second >= JEV.bars.margin));
      if (rest && sure && atEnd && memo.current.ignored < 2) ending = { text: `${spaced ? '' : ' '}${rest}`, kind: 'next' };
      if (!ending && why && !memo.current.whyUsed && atEnd && why.noul < JEV.bars.why) ending = { text: whyTail(typed), kind: 'why' };
    } else {
      if (asks.why && !memo.current.whyUsed && wordsIn(typed) >= 4 && atEnd && !/\b(because|so that|since|which meant)\b/i.test(typed)) {
        ending = { text: whyTail(typed), kind: 'why' };
      }
      if (!dismissed && wordsIn(typed) >= 6) setGuess(asks.soundsLike(typed));
    }
    setTail(ending);
  };

  const pauseThenThink = (typed: string) => {
    window.clearTimeout(memo.current.timer);
    memo.current.timer = window.setTimeout(() => void think(typed), memo.current.jev ? 450 : 700);
  };

  const write = (value: string) => {
    if (tail?.kind === 'next') memo.current.ignored++; // typed past a suggestion
    setTail(null);
    setText(value);
    pauseThenThink(value);
  };

  const put = (value: string) => {
    setTail(null);
    setText(value);
    const el = field.current;
    if (el) {
      el.focus();
      requestAnimationFrame(() => el.setSelectionRange(value.length, value.length));
    }
    pauseThenThink(value);
  };

  const takeTail = () => {
    if (!tail) return;
    if (tail.kind === 'why') memo.current.whyUsed = true;
    put(`${text}${tail.text} `);
  };

  const submit = () => {
    settleNow();
    if (!enough || closing) return field.current?.focus();
    const sounds = dismissed ? null : (guess ?? (memo.current.jev ? null : asks.soundsLike(text)));
    onSubmit(text.trim().replace(/\s+/g, ' '), sounds);
  };

  useHotkeys({ Escape: close });

  const guessed = guess ? answers.findIndex((answer) => answer.id === guess) : -1;
  // What sits under the words, keyed so a change rises in instead of popping.
  const under = !text.trim() ? 'starters' : dismissed ? 'kept' : guessed >= 0 ? `guess-${guessed}` : tail?.kind === 'why' ? 'why' : 'none';

  return (
    <div ref={box} className={`${styles.own} ${settled ? styles.ownSettled : ''}`} role="group" aria-label={asks.placeholder}>
      <div ref={was} className={styles.ownWas} aria-hidden>
        {answers.find((answer) => answer.ownWords)?.title}
      </div>
      <div className={styles.ownTop}>
        <div ref={art}>{ART.speech}</div>
        <button ref={shut} type="button" className={styles.ownClose} onClick={close} aria-label={COPY.ownWords.close}>
          <svg width="11" height="11" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div ref={middle} className={styles.ownMiddle}>
        <div className={styles.ownGhost} aria-hidden>
          {tail ? (
            <>
              <span>{text}</span>
              <span className={styles.ownTail}>{tail.text}</span>
              <span className={styles.ownTab}>{COPY.ownWords.tab}</span>
            </>
          ) : null}
        </div>
        <textarea
          ref={field}
          className={styles.ownField}
          rows={1}
          value={text}
          placeholder={asks.placeholder}
          aria-label={asks.placeholder}
          onChange={(event) => write(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            } else if (event.key === 'Tab' && tail) {
              event.preventDefault();
              takeTail();
            }
          }}
        />
      </div>
      <div className={styles.ownFoot}>
        <div ref={left} className={styles.ownLeftSlot}>
          <div key={under} className={styles.ownLeft}>
            {under === 'starters' ? (
              <>
                <span className={styles.ownLead}>{COPY.ownWords.startWith}</span>
                {asks.starters.map((starter) => (
                  <button key={starter} type="button" className={styles.starter} onClick={() => put(`${starter} `)}>
                    {starter}…
                  </button>
                ))}
              </>
            ) : under === 'kept' ? (
              <span className={styles.ownLead}>{COPY.ownWords.kept}</span>
            ) : guessed >= 0 ? (
              <>
                <span className={styles.ownLead}>{COPY.ownWords.soundsLike}</span>
                <span className={styles.guess}>
                  <span className={styles.guessKey}>{letterOf(guessed)}</span>
                  {answers[guessed].title}
                </span>
                <button
                  type="button"
                  className={styles.notQuite}
                  onClick={() => {
                    setDismissed(true);
                    setGuess(null);
                    field.current?.focus();
                  }}
                >
                  {COPY.ownWords.notQuite}
                </button>
              </>
            ) : under === 'why' ? (
              <span className={styles.ownLead}>{COPY.ownWords.whyNote}</span>
            ) : null}
          </div>
        </div>
        <button ref={send} type="button" className={styles.send} onClick={submit} disabled={!enough} aria-label={COPY.ownWords.send}>
          <svg width="12" height="14" viewBox="0 0 12 14" fill="none" aria-hidden="true">
            <path d="M6 13V1.5M1.5 6L6 1.5 10.5 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
